/**
 * ============================================
 * UTILIDADES DE FECHA Y HORA - ZONA HORARIA CHILE
 * ============================================
 * 
 * ⚠️ IMPORTANTE: Este proyecto opera en zona horaria de Chile (America/Santiago).
 * 
 * 🚫 NUNCA uses directamente:
 *    - new Date() para obtener fecha/hora actual
 *    - new Date().toISOString()
 *    - Date.now() para timestamps
 * 
 * ✅ SIEMPRE usa las funciones de este archivo que garantizan la zona horaria correcta,
 *    independiente de la configuración del servidor o navegador.
 * 
 * 📦 Dependencias: date-fns-tz
 */

import { format, toZonedTime } from 'date-fns-tz';

/**
 * Zona horaria del proyecto (Chile).
 * Maneja automáticamente el horario de verano (DST).
 */
const TIMEZONE = 'America/Santiago';

/**
 * Obtiene la fecha y hora actual en zona horaria de Chile.
 * 
 * @returns Date - Objeto Date ajustado a zona horaria de Santiago
 * 
 * @example
 * const ahora = getFechaHoraChile();
 * console.log(ahora); // 2026-08-13T14:30:00.000Z (en hora Chile)
 * 
 * @useCase
 * - Obtener timestamp actual del negocio
 * - Calcular diferencias de tiempo
 * - Comparar fechas
 */
export function getFechaHoraChile(): Date {
  return toZonedTime(new Date(), TIMEZONE);
}

/**
 * Obtiene la fecha actual en formato YYYY-MM-DD en zona horaria de Chile.
 * 
 * ⚠️ Usar SIEMPRE para:
 * - Abrir caja: fechaInicio
 * - Crear venta: fecha de registro
 * - Filtros de reportes
 * - Cualquier operación del negocio
 * 
 * @returns string - Fecha en formato YYYY-MM-DD
 * 
 * @example
 * const hoy = getFechaHoyChile();
 * console.log(hoy); // "2026-08-13"
 * 
 * @example
 * // ❌ MAL (depende del servidor)
 * const fecha = new Date().toISOString().split('T')[0];
 * 
 * // ✅ BIEN (siempre zona Chile)
 * const fecha = getFechaHoyChile();
 */
export function getFechaHoyChile(): string {
  const fechaChile = getFechaHoraChile();
  return formatFechaLocal(fechaChile);
}

/**
 * Obtiene la hora actual en formato HH:mm:ss en zona horaria de Chile.
 * 
 * @returns string - Hora en formato 24h (HH:mm:ss)
 * 
 * @example
 * const hora = getHoraActualChile();
 * console.log(hora); // "14:30:45"
 * 
 * @example
 * // Para apertura de caja
 * const horaInicio = getHoraActualChile(); // "09:00:00"
 */
export function getHoraActualChile(): string {
  const fechaChile = getFechaHoraChile();
  return format(fechaChile, 'HH:mm:ss', { timeZone: TIMEZONE });
}

/**
 * Convierte cualquier fecha a zona horaria de Chile.
 * 
 * @param fecha - Date o string ISO
 * @returns Date - Fecha ajustada a zona Chile
 * 
 * @example
 * const fechaUTC = new Date('2026-08-13T18:00:00Z');
 * const fechaChile = convertirAZonaChile(fechaUTC);
 * console.log(fechaChile.getHours()); // 14 (UTC-4 en invierno)
 * 
 * @example
 * const fechaISO = '2026-08-13T12:00:00-03:00';
 * const fechaChile = convertirAZonaChile(fechaISO);
 */
export function convertirAZonaChile(fecha: Date | string): Date {
  const date = typeof fecha === 'string' ? new Date(fecha) : fecha;
  return toZonedTime(date, TIMEZONE);
}

/**
 * Formatea una fecha a ISO string en zona horaria de Chile.
 * Útil para enviar al backend con timestamp correcto.
 * 
 * @param fecha - Fecha opcional (si no se pasa, usa fecha actual)
 * @returns string - Fecha en formato ISO
 * 
 * @example
 * const isoChile = toISOStringChile();
 * console.log(isoChile); // "2026-08-13T14:30:45.123Z"
 * 
 * @example
 * const fecha = new Date('2026-08-13');
 * const isoChile = toISOStringChile(fecha);
 */
export function toISOStringChile(fecha?: Date): string {
  const fechaChile = fecha ? convertirAZonaChile(fecha) : getFechaHoraChile();
  return fechaChile.toISOString();
}

/**
 * @deprecated Usar getFechaHoyChile() en su lugar.
 * Esta función depende de la hora local del navegador.
 */
export function getFechaHoyLocal(): string {
  return formatFechaLocal(new Date());
}

/**
 * Formatea una fecha a string YYYY-MM-DD HH:mm:ss en zona horaria de Chile.
 * NO convierte a UTC, mantiene la hora local de Chile.
 * 
 * Útil para enviar al backend cuando espera formato "YYYY-MM-DD HH:mm:ss".
 * 
 * @param fecha - Fecha opcional (si no se pasa, usa fecha actual Chile)
 * @returns string - Fecha en formato "YYYY-MM-DD HH:mm:ss"
 * 
 * @example
 * const fechaChile = getFechaHoraChile();
 * fechaChile.setHours(14, 30, 0);
 * formatFechaHoraLocal(fechaChile); // "2026-08-13 14:30:00"
 */
export function formatFechaHoraLocal(fecha?: Date): string {
  const f = fecha || getFechaHoraChile();
  const y = f.getFullYear();
  const m = String(f.getMonth() + 1).padStart(2, '0');
  const d = String(f.getDate()).padStart(2, '0');
  const h = String(f.getHours()).padStart(2, '0');
  const min = String(f.getMinutes()).padStart(2, '0');
  const s = String(f.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}:${s}`;
}

/**
 * Formatea un objeto Date a string YYYY-MM-DD.
 * 
 * @param date - Objeto Date
 * @returns string - Fecha en formato YYYY-MM-DD
 * 
 * @example
 * const fecha = new Date(2026, 7, 13); // Agosto 13
 * formatFechaLocal(fecha); // "2026-08-13"
 */
export function formatFechaLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Formatea un string de fecha del backend (YYYY-MM-DD o ISO completo) a dd-mm-yyyy
// SIN pasar por `new Date()`, que mueve el día por timezone.
// Reemplazo seguro de `new Date(str).toLocaleDateString('es-CL')`.
export function formatFechaCL(fechaStr: string | null | undefined): string {
  if (!fechaStr) return '';
  const datePart = fechaStr.split('T')[0];
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return fechaStr;
  return `${d}-${m}-${y}`;
}

/**
 * Calcula días transcurridos desde una fecha hasta ahora.
 * 
 * @param fechaIso - Fecha en formato ISO (YYYY-MM-DD o completo)
 * @returns number - Días transcurridos (0 si fecha inválida)
 * 
 * @example
 * diasDesde('2026-08-10'); // 3 (si hoy es 13 de agosto)
 * diasDesde('2026-08-13T10:00:00'); // 0 (mismo día)
 * diasDesde(null); // 0
 */
export function diasDesde(fechaIso: string | null | undefined): number {
  if (!fechaIso) return 0;
  const fecha = new Date(fechaIso);
  if (Number.isNaN(fecha.getTime())) return 0;
  return Math.max(Math.floor((Date.now() - fecha.getTime()) / 86_400_000), 0);
}

/**
 * Calcula el período anterior con la misma duración.
 * 
 * Útil para comparaciones en reportes (ej: este mes vs mes anterior).
 * 
 * @param desde - Fecha inicio del período actual (YYYY-MM-DD)
 * @param hasta - Fecha fin del período actual (YYYY-MM-DD)
 * @returns Objeto con desde/hasta del período anterior
 * 
 * @example
 * // Período: 1-7 agosto (7 días)
 * calcularPeriodoAnterior('2026-08-01', '2026-08-07');
 * // Retorna: { desde: '2026-07-25', hasta: '2026-07-31' }
 * 
 * @example
 * // Para comparar este mes con el anterior
 * const { desde, hasta } = calcularPeriodoAnterior('2026-08-01', '2026-08-31');
 */
// Antigüedad legible de un timestamp (ej. dataUpdatedAt de React Query) respecto a ahora.
export function formatearAntiguedad(timestamp: number | undefined | null): string {
  if (!timestamp) return '';
  const minutos = Math.floor((Date.now() - timestamp) / 60_000);
  if (minutos < 1) return 'recién actualizado';
  if (minutos < 60) return `actualizado hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `actualizado hace ${horas} h`;
  return `actualizado hace ${Math.floor(horas / 24)} d`;
}

// Calcula el rango "período anterior" con la misma lógica que usa el backend:
// misma cantidad de días, terminando el día inmediatamente antes de `desde`.
export function calcularPeriodoAnterior(desde: string, hasta: string): { desde: string; hasta: string } {
  const parseLocal = (fecha: string) => {
    const [y, m, d] = fecha.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const desdeDate = parseLocal(desde);
  const hastaDate = parseLocal(hasta);
  const cantidadDias = Math.round((hastaDate.getTime() - desdeDate.getTime()) / 86_400_000);

  const hastaAnterior = new Date(desdeDate);
  hastaAnterior.setDate(hastaAnterior.getDate() - 1);

  const desdeAnterior = new Date(hastaAnterior);
  desdeAnterior.setDate(desdeAnterior.getDate() - cantidadDias);

  return { desde: formatFechaLocal(desdeAnterior), hasta: formatFechaLocal(hastaAnterior) };
}

/**
 * Calcula el mismo período pero del año anterior.
 * 
 * Útil para comparaciones año a año en reportes.
 * 
 * @param desde - Fecha inicio del período actual (YYYY-MM-DD)
 * @param hasta - Fecha fin del período actual (YYYY-MM-DD)
 * @returns Objeto con desde/hasta del año anterior
 * 
 * @example
 * // Período: 1-31 agosto 2026
 * calcularAnioAnterior('2026-08-01', '2026-08-31');
 * // Retorna: { desde: '2025-08-01', hasta: '2025-08-31' }
 * 
 * @example
 * // Comparar ventas este año vs año pasado
 * const { desde, hasta } = calcularAnioAnterior('2026-08-01', '2026-08-31');
 */
export function calcularAnioAnterior(desde: string, hasta: string): { desde: string; hasta: string } {
  const parseLocal = (fecha: string) => {
    const [y, m, d] = fecha.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const desdeDate = parseLocal(desde);
  const hastaDate = parseLocal(hasta);

  const desdeAnioAnterior = new Date(desdeDate);
  desdeAnioAnterior.setFullYear(desdeAnioAnterior.getFullYear() - 1);

  const hastaAnioAnterior = new Date(hastaDate);
  hastaAnioAnterior.setFullYear(hastaAnioAnterior.getFullYear() - 1);

  return { desde: formatFechaLocal(desdeAnioAnterior), hasta: formatFechaLocal(hastaAnioAnterior) };
}
